import Immutable from 'immutable';
import memoizeOne from 'memoize-one';

import {
	webapi,
	createModuleState,
	REPORTED_EXCEPTION,
	updateEntityTimestamp,
	getPreference,
	createModelForState,
	setPreference,
} from 'ps-gui';
import { getLookup } from '../data/lookup';
import { getPhrase } from '../data/phrases';
import {
	createSearchModel,
	parseRoleId
} from './utils';

const EMPTY_LIST = Immutable.List([]);
const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('Submission');

export const STATUS_GROUPS = [
	'OPEN',
	'SUBMITTED',
	'CLOSED'
];

export function getDetailTabHeader(detail) {
	if (!detail) {
		return '';
	}
	const name = (detail.get('name') || '').trim();
	if (name) {
		return name;
	}
	if (detail.get('id')) {
		return getPhrase('Submission') + ' ' + detail.get('seqId');
	} else {
		return getPhrase('newSubmission');
	}
}

export function isEditable(statusId) {
	return statusId === null || statusId === 'SAVED_FOR_LATER' || statusId === 'RETURNED';
}

export const getRoleTypeId = memoizeOne(roleId => {
	const pr = parseRoleId(roleId);
	if (pr) {
		return pr.roleTypeId;
	} else {
		return null;
	}
});

function processDetail(detail) {
	if (!detail.roleId) {
		detail.roleId = getLookup('submissionPartyRole').getItems().getIn([0, 'key']);
	}
	if (!detail.typeId && detail.roleId) {
		detail.typeId = getLookup('submissionType.' + getRoleTypeId(detail.roleId)).getItems().getIn([0, 'key']);
	}
}

export default class SubmissionModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new SubmissionModel(this.#data.setIn(name, value));
		} else {
			return new SubmissionModel(this.#data.set(name, value));
		}
	}

	#deleteProperty(name) {
		if (Array.isArray(name)) {
			return new SubmissionModel(this.#data.deleteIn(name));
		} else {
			return new SubmissionModel(this.#data.delete(name));
		}
	}

	#setInitialized() {
		return new SubmissionModel(this.#data.set('initialized', true));
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		let model = this;
		model = await model.refreshSearch(true);
		return model.#setProperty('languageTimestamp', new Date().getTime());
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	setSelectedTab(tabId) {
		return this.#setProperty('selectedTabId', tabId);
	}

	closeTab(tabId) {
		if (tabId === 'list') {
			// seznam nelze zavrit
			return this;
		}
		return this.#deleteProperty(tabId).updateTabs().setSelectedTab('list');
	}

	closeSelectedTab() {
		const selectedTabId = this.#data.get('selectedTabId');
		return this.closeTab(selectedTabId);
	}

	async saveSubmission(submit, silent) {
		let pars = this.#data.get('wDetail').toJS();
		pars.submit = submit;
		let res = await webapi.execute('/api/proc/api_submission/saveSubmission', pars);
		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		if (silent) {
			return this;
		}
		updateEntityTimestamp('submission');
		return this.closeTab('wDetail');
	}

	async openDetail(id) {
		let res = await webapi.execute('/api/proc/api_submission/getSubmissionDetail', {
			id
		});
		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		processDetail(res);
		let tabId = isEditable(res.statusId) ? 'wDetail' : 'rDetail';
		const immRes = Immutable.fromJS(res);
		let model = this.#setProperty(tabId, immRes);
		if (tabId === 'wDetail') {
			model = model.#setProperty('wDetailOrig', immRes);
		}
		return model.updateTabs().setSelectedTab(tabId);
	}

	isWDetailModified() {
		const data = this.#data;
		const wDetail = data.get('wDetail');
		if (!wDetail) {
			return false;
		}
		return wDetail !== data.get('wDetailOrig');
	}

	setWDetailProperty(name, value) {
		if (!this.#data.get('wDetail')) {
			return this;
		}
		return this.#setProperty(['wDetail', name], value);
	}

	setComments(value) {
		return this.setWDetailProperty('comments', value);
	}

	setRoleId(value) {
		return this.setWDetailProperty('roleId', value);
	}

	setTypeId(value) {
		return this.setWDetailProperty('typeId', value);
	}

	setName(value) {
		return this.setWDetailProperty('name', value).updateTabs();
	}

	getWDetailId() {
		return this.#data.getIn(['wDetail', 'id']);
	}

	updateTabs() {
		const data = this.#data;
		let tabs = [{
			id: 'list',
			text: getPhrase('list')
		}];
		['rDetail', 'wDetail'].forEach(id => {
			const detail = data.get(id);
			if (detail) {
				tabs.push({
					id: id,
					text: getDetailTabHeader(detail)
				});
			}
		});
		return this.#setProperty('tabs', Immutable.fromJS(tabs));
	}

	getFileSizeSum() {
		const files = this.#data.get('files') || EMPTY_LIST;
		let res = 0;
		files.forEach(file => {
			if (!file.get('_deleted')) {
				res += file.get('size');
			}
		});
		return res;
	}

	toggleFileDeleted(idx) {
		if (!this.#data.get('wDetail')) {
			return this;
		}
		const path = [
			'wDetail',
			'files',
			idx,
			'_deleted'
		];
		return this.#setProperty(path, !this.#data.getIn(path));
	}

	async setStatusGroupFilter(key, value) {
		return this.#setProperty('statusGroupFilter', (this.#data.get('statusGroupFilter') || EMPTY_MAP).set(key, value)).refreshSearch();
	}

	getDisplayedSubmissionId() {
		const data = this.#data;
		const tabId = data.get('selectedTabId');
		if (tabId !== 'list') {
			return data.getIn([tabId, 'id']);
		} else {
			return null;
		}
	}

	async refreshSearch(dontSavePreference) {
		const statusGroupFilter = this.#data.get('statusGroupFilter') || EMPTY_MAP;

		if (!dontSavePreference) {
			setPreference(this.#getExtFilterPreferenceKey(), Immutable.Map({
				statusGroupFilter,
			}));
		}

		let fixedFilter = {
			status_group: STATUS_GROUPS.filter(key => statusGroupFilter.get(key))
		};

		let srch = this.getSearch().setFixedFilter(fixedFilter);
		srch = await srch.search(1);
		return this.setSearch(srch);
	}

	showOpenOverUnsavedDialog(newId) {
		return this.#setProperty('openOverUnsavedDialog', EMPTY_MAP.set('newId', newId));
	}

	hideOpenOverUnsavedDialog() {
		return this.#deleteProperty('openOverUnsavedDialog');
	}

	showCloseUnsavedDialog() {
		return this.#setProperty('closeUnsavedDialog', true);
	}

	hideCloseUnsavedDialog() {
		return this.#setProperty('closeUnsavedDialog', false);
	}

	showCancelDialog(submission) {
		return this.#setProperty('cancelDialog', submission);
	}

	hideCancelDialog() {
		return this.#setProperty('cancelDialog', false);
	}

	addFiles(newFiles) {
		if (!this.#data.get('wDetail')) {
			return this;
		}
		const files = (this.#data.getIn(['wDetail', 'files']) || EMPTY_LIST).concat(Immutable.fromJS(newFiles));
		return this.#setProperty(['wDetail', 'files'], files);
	}

	#getPreferenceKeyBase() {
		return '//subm//';
	}

	#getExtFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'extFilter';
	}

	initializePreferences() {
		let data = this.#data;
		let ef = getPreference(this.#getExtFilterPreferenceKey());
		if (!ef) {
			return this;
		}

		if (ef.get('statusGroupFilter')) {
			data = data.set('statusGroupFilter', ef.get('statusGroupFilter'));
		}
		return new SubmissionModel(data);
	}

	async initialize() {
		console.log('inicializuji podani');
		let srch = createSearchModel('/api/dq/sv_submission').setEntityName('submission').initializePreferences();
		let model = this;
		model = model.#setProperty('statusGroupFilter', Immutable.Map(STATUS_GROUPS.reduce((acc, curr) => {
			acc[curr] = true;
			return acc;
		}, {})));
		srch = srch.setProcessFilter(filter => {
			if (filter.role_id) {
				var pr = parseRoleId(filter.role_id);
				filter.party_id = pr.partyId;
				filter.role_type_id = pr.roleTypeId;
			}
			return filter;
		});
		model = model.setSearch(srch).#setInitialized();
		model = model.initializePreferences();
		model = await model.refreshSearch(true);
		return model;
	}

	setSearch(model) {
		return new SubmissionModel(this.#data.set('search', model.getState()));
	}
	getSearch() {
		return createModelForState(this.#data.get('search'));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

/*
- seznam
- zobrazeni readonly detailu (zatim bez historie)
- nahravani souboru
- validace (bud soubory nebo komentar)
- kontrola souctu velikosti souboru
- leva ikonka v tabu
- zavirani tabu
*/

